import React, { useEffect, useState } from "react";
import { clsx } from "clsx";
import { isRetailerOnly, RETAILER_ONLY_LABEL } from "@src/utilities/retailerOnly";

export const ProductTileQuickAdd = ({
  product, 
  isHovered, 
  settings, 
  isMobile, 
  onVariantChange, 
  onAddToCart
}) => {
  const [currentVariant, setCurrentVariant] = useState(null);

  // Get product context and match variants
  const productContext = (window as any).getProductContext?.();
  let variantId = null;
  let matchedVariant: any = null;

  if (productContext && productContext.variantId) {
    variantId = productContext.variantId;
  }

  // Loop through product.variants and match against product context or currentVariant
  if (product && product.variants && (productContext || currentVariant)) {
    product.variants.forEach(variant => {
      // Match by variant ID from product context
      if (variant.id === variantId || variant.id === productContext?.variantId) {
        matchedVariant = variant;
      }
      
      // Also match against currentVariant if set
      if (currentVariant && variant.id === currentVariant.id) {
        matchedVariant = { ...variant, ...currentVariant };
      }
      
      // Also match by current product variants if available
      if (productContext?.variants) {
        const contextVariant = productContext.variants.find(v => v.id === variant.id);
        if (contextVariant) {
          matchedVariant = { ...variant, ...contextVariant };
        }
      }
    });
  }
  

  // Retailer-only products stay on the grid but cannot be added to cart. The
  // tag wins over stock state, so tagged + sold out still reads "Retailer Only".
  const retailerOnly = isRetailerOnly(product);
  const soldOut = matchedVariant && matchedVariant.available === false;

  const {
    quick_add_display,
    button_type,
    button_type_enable_mobile_override,
    button_type_mobile,
    button_text,
    button_text_style,
    uppercase,
    icon_class,
    icon_class_mobile,
    icon_size,
    icon_size_mobile,
    button_height,
    button_height_enable_mobile_override,
    button_height_mobile,
    bottom_position,
    position_type,
    position_type_enable_mobile_override,
    position_type_mobile,
    button_radius,
    border_radius,
    width_type,
    width_percentage,
    width_pixels,
    padding_top,
    padding_right,
    padding_bottom,
    padding_left,
    margin_top,
    margin_right,
    margin_bottom,
    margin_left,
    classes
  } = settings;
  
  const buttonRadius = button_radius;
  const borderRadius = border_radius;

  // Determine current button type based on device and settings
  const getCurrentButtonType = () => {
    if (isMobile && button_type_enable_mobile_override) {
      return button_type_mobile;
    }
    return button_type;
  };

  // Get icon class based on device
  const getIconClass = () => {
    if (isMobile && button_type_enable_mobile_override && icon_class_mobile) {
      return icon_class_mobile;
    }
    return icon_class;
  };

  // Get icon size based on device
  const getIconSize = () => {
    if (isMobile && button_type_enable_mobile_override && icon_size_mobile) {
      return icon_size_mobile;
    }
    return icon_size;
  };

  // Get current position type based on device and settings
  const getCurrentPositionType = () => {
    if (isMobile && position_type_enable_mobile_override) {
      return position_type_mobile;
    }
    return position_type;
  };

  // Get current button height based on device and button type
  const getCurrentButtonHeight = () => {
    // Only apply height for text buttons
    if (getCurrentButtonType() !== 'text') {
      return null;
    }
    
    if (isMobile && button_height_enable_mobile_override) {
      return button_height_mobile;
    }
    return button_height;
  };

  // Get position classes based on position type
  const getPositionClasses = () => {
    const currentPositionType = getCurrentPositionType();
    if (currentPositionType === 'relative') {
      return `relative`;
    } else {  
      return `flex justify-center w-full ${currentPositionType}`;
    }
  };

  // Render button content based on type
  const renderButtonContent = () => {
    const currentButtonType = getCurrentButtonType();

    // Always render as text — an icon cannot communicate "Retailer Only".
    if (retailerOnly) {
      return RETAILER_ONLY_LABEL;
    }

    if (currentButtonType === 'icon') {
      const iconClass = getIconClass();
      const iconSize = getIconSize();
      
      return (
        <i 
          className={clsx(
            `icon-${iconClass}`,
            matchedVariant && matchedVariant.available === false ? 'opacity-50' : ''
          )}
          style={{ fontSize: `${iconSize}px` }}
          aria-label={matchedVariant && matchedVariant.available === false ? 'Out of stock' : (button_text || 'Add to cart')}
        />
      );
    } else {
      // Text button - show "Out of stock" if matched variant is unavailable
      if (matchedVariant && matchedVariant.available === false) {
        return 'Out of stock';
      }
      return button_text;
    }
  };
  
  const getQuickAddBorderRadiusClasses = () => {
    if (buttonRadius === 'rounded') {
      return 'rounded-full';
    } else if (buttonRadius === 'set_radius') {
      return `rounded-[${borderRadius}px]`;
    }
  };

  const getPaddingMarginClasses = () => {
    return `
      pt-[${padding_top}px]
      pr-[${padding_right}px]
      pb-[${padding_bottom}px]
      pl-[${padding_left}px]
      mt-[${margin_top}px]
      mr-[${margin_right}px]
      mb-[${margin_bottom}px]
      ml-[${margin_left}px]
    `;
  };

  const widthClasses = () => {
    // Don't apply width constraints for icon buttons
    if (getCurrentButtonType() === 'icon') {
      return '';
    }
    
    if (width_type === 'set_width') {
      return `w-[${width_pixels}px]`;
    } else {
      return `w-[${width_percentage}%]`;
    }
  };


  useEffect(() => {
    if (!isHovered) {
      setCurrentVariant(product.variants[0]);
    }

    if (isHovered && product?.variants?.length <= 1) {
      setCurrentVariant(product.variants[0]);
    }
  }, [isHovered]);

  const updateCurrentVariant = (variant) => {
    setCurrentVariant(variant);
    
    // Call the prop callback if provided
    if (onVariantChange) {
      onVariantChange(variant, product);
    }
  };

  const handleAddToCart = (e) => {
    // If custom onAddToCart prop is provided, use it instead of default form submission
    if (onAddToCart) {
      e.preventDefault();
      onAddToCart(currentVariant, product);
      return;
    }
    
    // Default behavior: let the form submit normally to /cart/add
  };

  // Retailer-only: replace the whole quick-add affordance (size picker included)
  // with a single inert label. The tile itself still renders as normal.
  if (retailerOnly) {
    return (
      <div
        className={clsx(
          getPositionClasses(),
          "cursor-default",
          !isMobile && quick_add_display === 'on_hover' ? 'opacity-0 group-hover:opacity-100' : '',
          getPaddingMarginClasses()
        )}
        style={{
          ...(getCurrentButtonHeight() && { height: `${getCurrentButtonHeight()}px` }),
          bottom: `${bottom_position}px`
        }}
      >
        <div
          aria-disabled="true"
          className={clsx(
            `flex justify-center items-center bg-white cursor-default ${classes}`,
            `${button_text_style}${uppercase ? ' uppercase' : ''}`,
            getQuickAddBorderRadiusClasses(),
            widthClasses()
          )}
          style={getCurrentButtonHeight() ? { height: `${getCurrentButtonHeight()}px` } : {}}
        >
          {RETAILER_ONLY_LABEL}
        </div>
      </div>
    );
  }

  return (
    <>
      {product?.variants?.length > 1 ? (
        <div
          className={clsx(
            getPositionClasses(),
            "cursor-default",
            !isMobile && quick_add_display === 'on_hover' ? 'opacity-0 group-hover:opacity-100' : '',
            getPaddingMarginClasses()
          )}
          style={{
            ...(getCurrentButtonHeight() && { height: `${getCurrentButtonHeight()}px` }),
            bottom: `${bottom_position}px`
          }}
        >
          {!currentVariant && (
            <div
              className={clsx(
                "productcard__quick-add",
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={{ background: "rgba(255, 255, 255, 0.40)" }}
            >
              {product?.variants?.map((variant) => (
                <React.Fragment key={variant?.id}>
                  {variant?.available ? (
                    <button
                      className="flex b__quick_option cursor-pointer"
                      onClick={() => updateCurrentVariant(variant)}
                    >
                      {variant?.option1 || variant?.options[0]}
                    </button>
                  ) : (
                    <div
                      className="flex b__quick_option text-black/20 cursor-default"
                    >
                      {variant?.option1 || variant?.options[0]}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {currentVariant && (
            <ajax-cart-product-form 
              className={clsx(
                "flex space-x-[28px] justify-center items-center bg-white",
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={getCurrentButtonHeight() ? { height: `${getCurrentButtonHeight()}px` } : {}}
            >
              <form
                action="/cart/add"
                id="product_form_id"
                className="product-form w-full flex"
                noValidate
                data-prodify-product-form
                onSubmit={handleAddToCart}
              >
                <div className="flex w-full space-x-6 justify-center h5">
                  <div className="flex">
                    <p className="uppercase">Size&nbsp;:&nbsp;{currentVariant.option1 || currentVariant.options[0]}&nbsp;</p>
                    <button onClick={() => updateCurrentVariant(null)} className={"underline"}>CHANGE</button>
                  </div>
                  <div className="text-black/20">|</div>
                  <input type="hidden" name="id" value={`${currentVariant.id}`} />
                  <button 
                    type="submit" 
                    disabled={matchedVariant && matchedVariant.available === false}
                    className={clsx(
                      getCurrentButtonType() === 'text' ? `${button_text_style}${uppercase ? ' uppercase' : ''}` : 'flex items-center justify-center'
                    )}
                  >
                    {renderButtonContent()}
                  </button>
                </div>

                <div
                  data-ajax-cart-errors="form"
                  className="text-xs text-error-1 mt-2"
                ></div>
              </form>
            </ajax-cart-product-form>
          )}
        </div>
      ) : (
        <div
          className={clsx(
            getPositionClasses(),
            "cursor-pointer",
            !isMobile && quick_add_display === 'on_hover' ? 'opacity-0 group-hover:opacity-100' : '',
            getPaddingMarginClasses()
          )}
          style={{
            ...(getCurrentButtonHeight() && { height: `${getCurrentButtonHeight()}px` }),
            bottom: `${bottom_position}px`
          }}
        >
          {currentVariant && (
            <button
              disabled={matchedVariant && matchedVariant.available === false}
              className={clsx(
                `flex justify-center items-center ${classes}`,
                getCurrentButtonType() === 'text' 
                  ? `space-x-[28px] ${button_text_style}${uppercase ? ' uppercase' : ''}` 
                  : '',
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={getCurrentButtonHeight() ? { height: `${getCurrentButtonHeight()}px` } : {}}
              onClick={handleAddToCart}
            >
              {renderButtonContent()}
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default ProductTileQuickAdd;